import {
  LeaveAbsenceKindCode,
  LeaveAreaCode,
  LeaveCalculationError,
  type LeaveDayPartCode,
} from '@gb-ticket/shared-types';
import { type FormEvent, type ReactNode, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { isIsoDate, orderedIsoRange } from '@/products/leave/features/leave-calendar-utils';
import { leaveKindHint, leaveKindLabel } from '@/products/leave/features/leave-labels';
import { leaveAreaEnabled, useLeaveAreas } from '@/products/leave/features/use-leave-areas';
import {
  formatLeaveDays,
  previewVacationDays,
} from '@/products/leave/features/preview-vacation-days';
import {
  useCreateLeaveRequest,
  useLeaveAbsenceTypes,
  useLeaveBalance,
  useLeaveBalanceProjection,
  useLeavePeople,
  useSubmitLeaveRequest,
} from '@/products/leave/features/use-leave';
import { LEAVE_REQUESTS_PATH } from '@/products/leave/leave-paths';

type Part = LeaveDayPartCode;

export function LeaveRequestPage() {
  usePageTitle('Abwesenheit beantragen');
  const copy = useVoiceCopy();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const preset = presetRange(search.get('from'), search.get('to'));
  const create = useCreateLeaveRequest();
  const submit = useSubmitLeaveRequest();
  const absenceTypes = useLeaveAbsenceTypes();
  const areas = useLeaveAreas();
  const sickOn = leaveAreaEnabled(areas.data, LeaveAreaCode.SICK) !== false;
  const overtimeOn = leaveAreaEnabled(areas.data, LeaveAreaCode.OVERTIME) !== false;
  const [step, setStep] = useState<0 | 1 | 2 | 3>(preset ? 1 : 0);
  const [absenceTypeId, setAbsenceTypeId] = useState<string | null>(null);
  const [startsOn, setStartsOn] = useState(preset?.from ?? '');
  const [endsOn, setEndsOn] = useState(preset?.to ?? '');
  const [startPart, setStartPart] = useState<Part>('full');
  const [endPart, setEndPart] = useState<Part>('full');
  const [query, setQuery] = useState('');
  const [substituteUserId, setSubstituteUserId] = useState<string | null>(null);
  const [substituteName, setSubstituteName] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const year = Number(startsOn.slice(0, 4)) || new Date().getFullYear();
  const balance = useLeaveBalance(year);
  const people = useLeavePeople(query);
  const types = (absenceTypes.data?.items ?? []).filter((type) => {
    if (type.kind === LeaveAbsenceKindCode.SICK && !sickOn) {
      return false;
    }
    if (type.kind === LeaveAbsenceKindCode.COMP_TIME && !overtimeOn) {
      return false;
    }
    return true;
  });
  const selected = types.find((item) => item.id === absenceTypeId) ?? null;
  const kind = selected?.kind ?? LeaveAbsenceKindCode.VACATION;
  const isSick = kind === LeaveAbsenceKindCode.SICK;
  const needsSubstitute = selected?.requiresApproval ?? true;

  const preview = previewFor({
    startsOn,
    endsOn,
    startPart,
    endPart,
    pattern: balance.data?.pattern ?? null,
    holidays: balance.data?.holidays ?? [],
    maxDaysPerRequest: selected?.maxDaysPerRequest,
  });
  const projection = useLeaveBalanceProjection({
    startsOn,
    endsOn,
    startPart,
    endPart,
    enabled: kind === LeaveAbsenceKindCode.VACATION && preview.days != null,
  });
  const restAfter =
    kind === LeaveAbsenceKindCode.VACATION ? (projection.data?.restAfterPending ?? null) : null;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      if (!absenceTypeId) {
        setError(copy('leave.request.kind.required'));
        return;
      }
      const draft = await create.mutateAsync({
        absenceTypeId,
        startsOn,
        endsOn,
        startPart,
        endPart,
        comment: comment.trim() === '' ? null : comment.trim(),
        substituteUserId: needsSubstitute ? substituteUserId : null,
      });
      await submit.mutateAsync(draft.id);
      void navigate(LEAVE_REQUESTS_PATH);
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError ? caught.message : 'Einreichen ist fehlgeschlagen.',
      );
    }
  }

  function nextFromDates() {
    setError(null);
    setStep(needsSubstitute ? 2 : 3);
  }

  return (
    <form className="space-y-6" onSubmit={(event) => void onSubmit(event)}>
      <PlatformPageHeader
        title="Abwesenheit beantragen"
        description={
          selected
            ? `${selected.labelDe}. ${leaveKindHint(kind)} Keine medizinischen Angaben.`
            : 'Keine medizinischen Angaben in den Kommentar schreiben.'
        }
      />
      {balance.isError ? (
        <p className="text-sm text-destructive">
          {balance.error instanceof ApiRequestError
            ? balance.error.message
            : 'Der Saldo konnte nicht geladen werden.'}
        </p>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {step === 0 ? (
        <fieldset className={cn(leavePanelClassName, 'space-y-4')}>
          <legend className="text-lg font-semibold">Art der Abwesenheit</legend>
          {absenceTypes.isPending ? (
            <p className="text-sm text-muted-foreground">Arten werden geladen …</p>
          ) : null}
          {absenceTypes.isError ? (
            <p className="text-sm text-destructive">
              {absenceTypes.error instanceof ApiRequestError
                ? absenceTypes.error.message
                : 'Die Arten konnten nicht geladen werden.'}
            </p>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-2">
            {types.map((type) => (
              <Button
                key={type.id}
                type="button"
                variant={type.id === absenceTypeId ? 'default' : 'outline'}
                className="h-auto justify-start py-3 text-left"
                onClick={() => {
                  setAbsenceTypeId(type.id);
                  if (!type.requiresApproval) {
                    setSubstituteUserId(null);
                    setSubstituteName(null);
                  }
                }}
              >
                <span className="flex flex-col items-start gap-0.5">
                  <span>{type.labelDe}</span>
                  <span className="text-xs font-normal opacity-80">{leaveKindHint(type.kind)}</span>
                </span>
              </Button>
            ))}
          </div>
          <Button type="button" disabled={!absenceTypeId} onClick={() => setStep(1)}>
            Weiter
          </Button>
        </fieldset>
      ) : null}

      {step === 1 ? (
        <fieldset className={cn(leavePanelClassName, 'space-y-4')}>
          <legend className="text-lg font-semibold">Zeitraum</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Von" htmlFor="startsOn">
              <Input
                id="startsOn"
                type="date"
                required
                value={startsOn}
                onChange={(event) => setStartsOn(event.target.value)}
              />
            </Field>
            <Field label="Bis" htmlFor="endsOn">
              <Input
                id="endsOn"
                type="date"
                required
                value={endsOn}
                onChange={(event) => setEndsOn(event.target.value)}
              />
            </Field>
            <PartField
              id="startPart"
              label="Erster Tag"
              value={startPart}
              onChange={setStartPart}
            />
            <PartField id="endPart" label="Letzter Tag" value={endPart} onChange={setEndPart} />
          </div>
          {!absenceTypeId ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm text-muted-foreground">Bitte zuerst die Art wählen.</p>
              <Button type="button" variant="outline" onClick={() => setStep(0)}>
                Art wählen
              </Button>
            </div>
          ) : null}
          <p className="text-sm text-muted-foreground">{preview.text}</p>
          {restAfter != null && balance.data ? (
            <p className="text-sm">
              Rest danach {formatLeaveDays(restAfter)}. Davon{' '}
              {formatLeaveDays(balance.data.carryOver)} Übertrag bis{' '}
              {formatGermanDate(balance.data.carryOverExpiresOn)}.
            </p>
          ) : null}
          {kind === LeaveAbsenceKindCode.COMP_TIME && balance.data ? (
            <p className="text-sm">
              Überstundenkonto:{' '}
              {new Intl.NumberFormat('de-DE', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              }).format(balance.data.overtimeHours)}{' '}
              h
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setStep(0)}>
              Zurück
            </Button>
            <Button
              type="button"
              disabled={!absenceTypeId || !startsOn || !endsOn || preview.days == null}
              onClick={() => nextFromDates()}
            >
              Weiter
            </Button>
          </div>
        </fieldset>
      ) : null}

      {step === 2 && needsSubstitute ? (
        <fieldset className={cn(leavePanelClassName, 'space-y-4')}>
          <legend className="text-lg font-semibold">Vertretung</legend>
          <Field label="Person suchen" htmlFor="substitute">
            <Input
              id="substitute"
              value={query}
              placeholder="Mindestens zwei Buchstaben"
              onChange={(event) => setQuery(event.target.value)}
            />
          </Field>
          {substituteName ? <p className="text-sm">Gewählt: {substituteName}</p> : null}
          <ul className="space-y-1">
            {people.data?.items.map((person) => (
              <li key={person.id}>
                <Button
                  type="button"
                  variant={person.id === substituteUserId ? 'default' : 'outline'}
                  onClick={() => {
                    setSubstituteUserId(person.id);
                    setSubstituteName(person.displayName);
                  }}
                >
                  {person.displayName}
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setStep(1)}>
              Zurück
            </Button>
            <Button type="button" onClick={() => setStep(3)}>
              Weiter
            </Button>
          </div>
        </fieldset>
      ) : null}

      {step === 3 ? (
        <fieldset className={cn(leavePanelClassName, 'space-y-4')}>
          <legend className="text-lg font-semibold">Prüfen</legend>
          <p className="text-sm text-muted-foreground">
            {selected?.labelDe ?? leaveKindLabel(kind)}
          </p>
          <p>
            {formatGermanDate(startsOn)} bis {formatGermanDate(endsOn)} ·{' '}
            {preview.days == null ? '—' : `${formatLeaveDays(preview.days)} Arbeitstage`}
          </p>
          {needsSubstitute ? (
            <p className="text-sm text-muted-foreground">Vertretung: {substituteName ?? 'keine'}</p>
          ) : null}
          {isSick ? (
            <p className="text-sm text-muted-foreground">{copy('leave.request.sick.notice')}</p>
          ) : null}
          <Field label="Hinweis" htmlFor="comment">
            <Input
              id="comment"
              value={comment}
              maxLength={500}
              onChange={(event) => setComment(event.target.value)}
            />
          </Field>
          <p className="text-sm text-muted-foreground">Keine medizinischen Angaben.</p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(needsSubstitute ? 2 : 1)}
            >
              Zurück
            </Button>
            <Button
              type="submit"
              disabled={create.isPending || submit.isPending || preview.days == null}
            >
              {isSick ? 'Erfassen' : 'Einreichen'}
            </Button>
          </div>
        </fieldset>
      ) : null}
    </form>
  );
}

function presetRange(from: string | null, to: string | null): { from: string; to: string } | null {
  if (!from || !to || !isIsoDate(from) || !isIsoDate(to)) {
    return null;
  }
  return orderedIsoRange(from, to);
}

function previewFor(input: {
  startsOn: string;
  endsOn: string;
  startPart: LeaveDayPartCode;
  endPart: LeaveDayPartCode;
  pattern: Parameters<typeof previewVacationDays>[0]['weights'] | null;
  holidays: readonly string[];
  maxDaysPerRequest?: number | null;
}): { days: number | null; text: string } {
  if (!input.startsOn || !input.endsOn || !input.pattern) {
    return {
      days: null,
      text: input.pattern
        ? 'Bitte einen Zeitraum wählen.'
        : 'Die Arbeitswoche ist noch nicht hinterlegt.',
    };
  }
  try {
    const days = previewVacationDays({ ...input, weights: input.pattern });
    return { days, text: `${formatLeaveDays(days)} Arbeitstage.` };
  } catch (error) {
    if (error instanceof LeaveCalculationError) {
      return { days: null, text: previewError(error.code) };
    }
    return { days: null, text: 'Der Zeitraum kann nicht berechnet werden.' };
  }
}

function previewError(code: LeaveCalculationError['code']): string {
  switch (code) {
    case 'ENDS_BEFORE_START':
      return 'Das Enddatum liegt vor dem Startdatum.';
    case 'SPAN_TOO_LONG':
      return 'Ein Antrag darf höchstens 60 Kalendertage umfassen.';
    case 'MAX_DAYS':
      return 'Der Antrag überschreitet die Höchstzahl an Tagen für diese Art.';
    case 'HALF_ON_FREE_DAY':
      return 'Ein halber Tag ist an einem freien Tag nicht möglich.';
    case 'NO_WORKDAY':
      return 'In diesem Zeitraum liegt kein Arbeitstag.';
    default:
      return 'Die Arbeitswoche ist noch nicht hinterlegt.';
  }
}

function PartField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: LeaveDayPartCode;
  onChange: (value: LeaveDayPartCode) => void;
}) {
  return (
    <Field label={label} htmlFor={id}>
      <Select value={value} onValueChange={(next) => onChange(next as LeaveDayPartCode)}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="full">Ganzer Tag</SelectItem>
          <SelectItem value="morning">Nur Vormittag</SelectItem>
          <SelectItem value="afternoon">Nur Nachmittag</SelectItem>
        </SelectContent>
      </Select>
    </Field>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function formatGermanDate(iso: string): string {
  if (!iso) {
    return '—';
  }
  return new Intl.DateTimeFormat('de-DE').format(new Date(`${iso}T12:00:00`));
}
